import { DefaultQueryParams } from '@frontend/api-utils';
import { EntityType } from '@frontend/common';
import { MultiObjectSelectInput } from '@frontend/components/basic-forms';
import { FunctionalitySelectV2 } from '@frontend/functionality/select';
import { Application, Feature } from '@frontend/functionality/types';
import { IGroup } from '@frontend/group/types';
import { CommonMessage } from '@frontend/lang';
import { updateField } from '@frontend/repository';
import { SlotStatus } from '@frontend/slot/types';
import { ToastUtil } from '@frontend/toast-utils';
import { TransactionPuDoClient, TransactionPudoExtendedClient, TransactionWorkflowClient } from '@frontend/transaction/api';
import { CreatePudoTransaction, ExtendedPudoTransaction, TransactionTriggerName, TransactionType } from '@frontend/transaction/types';
import { SelectedUserDetails } from '@frontend/user/select';
import { IUser } from '@frontend/user/types';
import { lazy, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CreateModal from '../../../modal/create-modal/create-modal.component';
import {
    CreateTransactionReceiverOption,
    fetchCreateTransactionContacts,
    fetchCreateTransactionReceivers,
    formatCreateTransactionPartyLabel,
    useCreateTransactionModal
} from './create-transaction-modal.controller';

const AvailableSlotsSelect = lazy(() => import('@frontend/slot/select').then((module) => ({ default: module.AvailableSlotsSelect })));

export interface CreateTransactionModalProps {
    formId?: string;
    application?: Application;
    feature?: Feature;
}

export const CreateTransactionModal = (props: CreateTransactionModalProps) => {
    const viewProps = useCreateTransactionModal(props);
    const [trackingNumberValid, setTrackingNumberValid] = useState<boolean>(true);
    const intl = useIntl();

    const usersLabel = intl.formatMessage({ id: 'Objects.USER.PLURAL', defaultMessage: 'Users', description: 'Plural name for users' });
    const userGroupsLabel = intl.formatMessage({ id: 'Objects.USER_GROUP.PLURAL', defaultMessage: 'User groups', description: 'Plural name for user groups' });

    return (
        <CreateModal<ExtendedPudoTransaction, CreatePudoTransaction>
            onSubmitSuccess={(v) =>
                TransactionWorkflowClient.triggerTransactionState(v.account_id, v.id, { trigger: TransactionTriggerName.TRANSACTION_PROCESS })
            }
            type={EntityType.TRANSACTION}
            submitButtonOverride={
                <FormattedMessage
                    id='feature.transactions.create-transaction'
                    defaultMessage='Create transaction'
                />
            }
            label={
                <FormattedMessage
                    id='feature.transactions.create-transaction'
                    defaultMessage='Create transaction'
                />
            }
            title={CommonMessage.FORMS.BASIC_INFORMATION}
            order={['tracking_number', 'senders', 'receivers', 'spot_id', ['module_id', 'slot_id'], 'shipping_notes']}
            required={['senders', 'receivers', 'spot_id', ['module_id', 'slot_id']]}
            keyOverwrite={
                new Map([
                    ['shipping_notes', CommonMessage.OBJECTS.TRANSACTION.SHIPPING_NOTES],
                    ['tracking_number', CommonMessage.OBJECTS.TRANSACTION.TRACKING_NUMBER]
                ])
            }
            initialValue={{ type: TransactionType.PUDO, application: Application.PUDO }}
            fieldProps={(formValues) =>
                new Map([
                    [
                        'slot_id',
                        {
                            disabled: !formValues.spot_id,
                            isClearable: true,
                            queryParams: { spot_id: formValues.spot_id, module_id: formValues.module_id }
                        }
                    ],
                    ['spot_id', { isClearable: true }],
                    ['module_id', { isClearable: true, disabled: !formValues.spot_id, queryParams: { spot_id: formValues.spot_id } }],
                    [
                        'tracking_number',
                        {
                            onBlur: async () => {
                                if (!formValues.tracking_number) return;
                                await TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                                    account_id: viewProps.currentAccount,
                                    tracking_number: formValues.tracking_number,
                                    // Validation doesn't render collis/packages/PIN-codes.
                                    without_collis: 'true',
                                    without_packages: 'true',
                                    without_pin_codes: 'true'
                                }).then((res) => {
                                    if (res.count !== 0 && res.results.some((result) => result.tracking_number === formValues.tracking_number)) {
                                        setTrackingNumberValid(false);
                                        ToastUtil.warning(CommonMessage.ERRORS.TRACKING_NUMBER_EXISTS);
                                        return;
                                    } else {
                                        setTrackingNumberValid(true);
                                        return;
                                    }
                                });

                                return;
                            }
                        }
                    ]
                ])
            }
            formId={props.formId}
            onSubmit={(v) => {
                const allReceiverIds = viewProps.getAllReceiverUserIds();
                const payload = { ...v, receivers: allReceiverIds };

                if (viewProps.functionalities.length === 0) {
                    return TransactionPuDoClient.postPudoTransaction(viewProps.currentAccount, payload);
                } else {
                    return TransactionPuDoClient.postPudoTransaction(viewProps.currentAccount, {
                        ...payload,
                        feature: viewProps.selectedFunctionality?.feature
                    });
                }
            }}
            expectedType={
                new Map([
                    ['senders', 'entity'],
                    ['receivers', 'entity'],
                    ['shipping_notes', 'textarea']
                ])
            }
            submitDisabled={(formValues) =>
                !formValues.senders?.length ||
                (!formValues.receivers?.length && viewProps.selectedGroupIds.length === 0) ||
                !formValues.slot_id ||
                !trackingNumberValid
            }
            append={
                <>
                    {viewProps.hasColliWorkflow && (
                        <p className='text-foreground-muted text-sm'>
                            <FormattedMessage
                                id='platform.applications.pudo.create-transaction-modal.multi-parcel-wizard-hint'
                                description='Hint that multi-parcel PuDo create should use the create wizard when colli workflow is configured'
                                defaultMessage='This form creates a single destination. For multi-parcel transactions, use the create wizard with parcel information.'
                            />
                        </p>
                    )}
                    {viewProps.functionalities.length > 1 ? (
                        <FunctionalitySelectV2
                            subset={viewProps.functionalities.map((f) => f.id)}
                            value={viewProps.selectedFunctionality?.feature}
                            onChangeObject={viewProps.setSelectedFunctionality}
                            isClearable
                            required
                        />
                    ) : null}
                </>
            }
            fieldOverwrite={
                new Map([
                    [
                        'senders',
                        <div
                            key='senders-select'
                            className='flex w-full flex-col gap-1'>
                            <MultiObjectSelectInput<IUser, DefaultQueryParams>
                                fetch={(arg) => fetchCreateTransactionContacts(arg, viewProps.currentAccount)}
                                itemLabel={formatCreateTransactionPartyLabel}
                                label={CommonMessage.OBJECTS.TRANSACTION.SENDERS}
                                onChange={(value) => {
                                    if (viewProps.form && props.formId) {
                                        viewProps.dispatch(updateField({ formId: props.formId, key: 'senders', value: value.map((c) => c.id) }));
                                    }
                                }}
                                value={viewProps.form?.value?.senders ?? []}
                            />
                            {(viewProps.form?.value?.senders ?? []).map((senderId: string) => (
                                <SelectedUserDetails
                                    key={senderId}
                                    userId={senderId}
                                />
                            ))}
                        </div>
                    ],
                    [
                        'receivers',
                        <div
                            key='receivers-select'
                            className='flex w-full flex-col gap-1'>
                            <MultiObjectSelectInput<CreateTransactionReceiverOption, DefaultQueryParams>
                                fetch={(arg) =>
                                    fetchCreateTransactionReceivers(arg, viewProps.currentAccount, { users: usersLabel, userGroups: userGroupsLabel })
                                }
                                mapOptions={'receiverType' as keyof CreateTransactionReceiverOption}
                                itemLabel={formatCreateTransactionPartyLabel}
                                label={CommonMessage.OBJECTS.TRANSACTION.RECEIVERS}
                                onChange={(value) => {
                                    const users = value.filter((v): v is IUser & { receiverType: string } => 'first_name' in v);
                                    const groups = value.filter((v): v is IGroup & { receiverType: string } => !('first_name' in v));
                                    viewProps.handleReceiverChange(users, groups);
                                }}
                                value={[...(viewProps.form?.value?.receivers ?? []), ...viewProps.selectedGroupIds]}
                            />
                            {(viewProps.form?.value?.receivers ?? []).map((receiverId: string) => (
                                <SelectedUserDetails
                                    key={receiverId}
                                    userId={receiverId}
                                />
                            ))}
                        </div>
                    ],
                    [
                        'slot_id',
                        <AvailableSlotsSelect
                            key={`slot-select-${viewProps.form?.value?.spot_id ?? 'none'}-${viewProps.form?.value?.module_id ?? 'none'}-${viewProps.selectedFunctionality?.id ?? 'none'}`}
                            constraints={{
                                account_id: viewProps.currentAccount,
                                transaction_type: TransactionType.PUDO,
                                spot_id: viewProps.form?.value?.spot_id,
                                application: viewProps.selectedFunctionality?.application ?? props.application ?? Application.PUDO,
                                feature: viewProps.selectedFunctionality?.feature ?? props.feature ?? null,
                                functionality_id: viewProps.selectedFunctionality?.id ?? null
                            }}
                            queryParams={{ status: SlotStatus.AVAILABLE, module_id: viewProps.form?.value?.module_id || undefined }}
                            submitted={false}
                            spot_id={viewProps.form?.value?.spot_id || null}
                            module_id={viewProps.form?.value?.module_id || null}
                            label={CommonMessage.OBJECTS.SLOT.DEFAULT}
                            onChange={(v) => {
                                if (viewProps.form && props.formId) {
                                    viewProps.dispatch(updateField({ formId: props.formId, key: 'slot_id', value: v }));
                                }
                            }}
                            required
                            value={viewProps.form?.value?.slot_id || null}
                            disabled={!viewProps.form?.value?.spot_id || !viewProps.form?.value?.module_id}
                            isClearable
                        />
                    ]
                ])
            }
            {...viewProps}
        />
    );
};
